import React, { FC, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Page,
  PageHeader,
  SkipToContent,
  PageSection,
  PageSectionVariants,
  Dropdown,
  DropdownToggle,
  PageHeaderTools,
  DropdownItem,
  FlexItem,
  Flex,
} from '@patternfly/react-core';
import logo from '@app/bgimages/logo_phase_slash.svg';
import { useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { BugIcon, CatalogIcon, EnterpriseIcon, GithubIcon, PowerOffIcon, UserIcon } from '@patternfly/react-icons';
import { deploymentModalKey } from '@app/pages/Deployments/Deployments';
import { baseUrl } from 'src';

interface IAppLayout {
  children: ReactNode;
}

const AppLayout: FC<IAppLayout> = ({ children }) => {
  const { keycloak } = useKeycloak();

  function LogoImg() {
    const history = useHistory();
    function handleClick() {
      history.push('/');
    }
    return <img src={logo} onClick={handleClick} alt="Phase Two" style={{ maxHeight: '24px' }} />;
  }

  const HeaderToolsDropdown = () => {
    const [isHeaderDropdownOpen, setIsHeaderDropdownOpen] = useState(false);

    return (
      <PageHeaderTools>
        <Dropdown
          isPlain
          position="right"
          id="user-dropdown"
          isOpen={isHeaderDropdownOpen}
          toggle={
            <DropdownToggle onToggle={setIsHeaderDropdownOpen} style={{ color: 'black' }}>
              {keycloak.tokenParsed?.preferred_username || keycloak.tokenParsed?.email || 'User'}
            </DropdownToggle>
          }
          dropdownItems={[
            <DropdownItem
              key="profile"
              id="profile"
              className="app-link"
              icon={<UserIcon />}
              href={`${baseUrl}/${keycloak.realm}/portal/profile`}
            >
              Profile
            </DropdownItem>,
            <DropdownItem
              key="log out"
              id="log out"
              className="app-link"
              onClick={() => {
                localStorage.removeItem(deploymentModalKey);
                keycloak.logout();
              }}
              icon={<PowerOffIcon />}
            >
              Log out
            </DropdownItem>,
          ]}
        />
      </PageHeaderTools>
    );
  };

  const Header = (
    <PageHeader
      logo={<LogoImg />}
      headerTools={
        <Flex justifyContent={{ default: 'justifyContentFlexEnd' }}>
          <Flex
            justifyContent={{ default: 'justifyContentFlexEnd' }}
            alignItems={{ default: 'alignItemsCenter' }}
            spaceItems={{ default: 'spaceItemsLg' }}
          >
            <a
              href={`${baseUrl}/${keycloak.realm}/portal/organizations`}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'black', textDecoration: 'none' }}
            >
              <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                <FlexItem>
                  <EnterpriseIcon size="sm" />
                </FlexItem>
                <FlexItem>Org Admin</FlexItem>
              </Flex>
            </a>
            <a
              href={'mailto:support@phasetwo.io'}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'black', textDecoration: 'none' }}
            >
              <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                <FlexItem>
                  <BugIcon size="sm" />
                </FlexItem>
                <FlexItem>Support</FlexItem>
              </Flex>
            </a>
            <a
              href={'https://phasetwo.io/docs/introduction/'}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'black', textDecoration: 'none' }}
            >
              <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                <FlexItem>
                  <CatalogIcon size="sm" />
                </FlexItem>
                <FlexItem>Docs</FlexItem>
              </Flex>
            </a>
            <a
              href={'https://github.com/p2-inc'}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'black', textDecoration: 'none' }}
            >
              <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                <FlexItem>
                  <GithubIcon size="sm" />
                </FlexItem>
              </Flex>
            </a>
            <HeaderToolsDropdown />
          </Flex>
        </Flex>
      }
    />
  );

  const pageId = 'primary-app-container';

  const PageSkipToContent = (
    <SkipToContent
      onClick={(event) => {
        event.preventDefault();
        const primaryContentContainer = document.getElementById(pageId);
        primaryContentContainer && primaryContentContainer.focus();
      }}
      href={`#${pageId}`}
    >
      Skip to Content
    </SkipToContent>
  );
  return (
    <Page
      mainContainerId={pageId}
      header={Header}
      skipToContent={PageSkipToContent}
      style={{ maxWidth: 'var(--pf-global--breakpoint--2xl)', margin: 'auto' }}
    >
      <PageSection variant={PageSectionVariants.light}>{children}</PageSection>
    </Page>
  );
};

export { AppLayout };
